import { useState, useEffect } from 'react'
import { Waves, Trophy, Users2, CheckCircle2, CreditCard, DollarSign, Smartphone } from 'lucide-react'
import axios from 'axios'
import MembershipForm from '../components/MembershipForm'
import SquarePayment from '../components/SquarePayment'

const Membership = () => {
  const [showForm, setShowForm] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [formData, setFormData] = useState(null)
  const [showQuestionnaire, setShowQuestionnaire] = useState(false)
  const [questionStep, setQuestionStep] = useState(1)
  const [answers, setAnswers] = useState({
    isReturning: null,
    isSenior: null,
    isSingle: null
  })
  const [selectedMembership, setSelectedMembership] = useState(null)
  const [poolSettings, setPoolSettings] = useState(null)

  // Fetch pool settings for opening date
  useEffect(() => {
    const fetchPoolSettings = async () => {
      try {
        const response = await axios.get('/api/pool-settings')
        setPoolSettings(response.data)
      } catch (err) {
        console.error('Error fetching pool settings:', err)
      }
    }
    fetchPoolSettings()
  }, [])

  // Check if current date is after May 1st of current year
  const isAfterMay1 = () => {
    const today = new Date()
    const currentYear = today.getFullYear()
    const may1 = new Date(currentYear, 4, 1) // Month is 0-indexed, so 4 = May
    return today >= may1
  }

  // Price increase amount after May 1st
  const priceIncrease = isAfterMay1() ? 100 : 0

  // Base prices (before May 1st)
  const basePrices = {
    returningHousehold: 675,
    returningSingleSenior: 475,
    newHousehold: 1175, // includes $500 initiation fee
    newSingleSenior: 975  // includes $500 initiation fee
  }

  const membershipTypes = {
    returning: {
      household: {
        price: basePrices.returningHousehold + priceIncrease,
        basePrice: basePrices.returningHousehold,
        title: 'Household Membership',
        description: 'Perfect for families - includes all household members',
        features: [
          'Access to pool and tennis facilities',
          'Unlimited family members',
          'Priority event registration',
          'Facility reservation privileges',
          'Member-only social events'
        ]
      },
      singleSenior: {
        price: basePrices.returningSingleSenior + priceIncrease,
        basePrice: basePrices.returningSingleSenior,
        title: 'Single/Senior Membership',
        description: 'Individual membership for singles and seniors (65+)',
        features: [
          'Full access to all facilities',
          'Tennis court reservations',
          'Pool privileges',
          'Social event participation',
          'Facility rental options'
        ]
      }
    },
    new: {
      household: {
        price: basePrices.newHousehold + priceIncrease,
        basePrice: basePrices.returningHousehold,
        initiationFee: 500,
        title: 'New Household Membership',
        description: 'Welcome to our community! Includes one-time initiation fee',
        features: [
          'One-time $500 initiation fee',
          'Access to pool and tennis facilities',
          'Unlimited family members',
          'Priority event registration',
          'Facility reservation privileges',
          'Member-only social events'
        ]
      },
      singleSenior: {
        price: basePrices.newSingleSenior + priceIncrease,
        basePrice: basePrices.returningSingleSenior,
        initiationFee: 500,
        title: 'New Single/Senior Membership',
        description: 'Individual membership with one-time initiation fee',
        features: [
          'One-time $500 initiation fee',
          'Full access to all facilities',
          'Tennis court reservations',
          'Pool privileges',
          'Social event participation',
          'Facility rental options'
        ]
      }
    }
  }

  const paymentMethods = [
    {
      name: 'Zelle',
      icon: 'smartphone',
      description: 'Instant transfer, no fees',
      email: 'admin@lockridgeforestswimtennis.com',
      fee: 0,
      recommended: true
    },
    {
      name: 'PayPal',
      icon: 'creditcard',
      description: 'Credit/Debit cards accepted',
      returningFee: 25,
      newFee: 40,
      recommended: false
    },
    {
      name: 'Square',
      icon: 'creditcard',
      description: 'Coming soon - Secure online payment',
      status: 'coming_soon',
      recommended: false
    }
  ]

  const handleFormSubmit = (data) => {
    setFormData(data)
    setShowForm(false)
    setShowPayment(true)
  }

  const handlePaymentComplete = () => {
    setShowPayment(false)
    setFormData(null)
    // Success message could be shown here
  }

  const handleStartQuestionnaire = () => {
    setShowQuestionnaire(true)
    setQuestionStep(1)
    setAnswers({
      isReturning: null,
      isSenior: null,
      isSingle: null
    })
    setSelectedMembership(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleAnswer = (question, answer) => {
    const newAnswers = { ...answers, [question]: answer }
    setAnswers(newAnswers)

    if (question === 'isReturning') {
      setQuestionStep(2)
    } else if (question === 'isSenior') {
      setQuestionStep(3)
    } else if (question === 'isSingle') {
      // Determine membership based on all answers
      determineMembership(newAnswers)
    }
  }

  const determineMembership = (finalAnswers) => {
    const { isReturning, isSenior, isSingle } = finalAnswers
    const status = isReturning ? 'returning' : 'new'

    let membership
    if (isSenior) {
      // Seniors always get single/senior pricing ($475)
      // but can add household members if they chose household
      membership = {
        ...membershipTypes[status].singleSenior,
        membershipTypeValue: 'senior',
        includeHousehold: !isSingle
      }
    } else if (isSingle) {
      membership = {
        ...membershipTypes[status].singleSenior,
        membershipTypeValue: 'single'
      }
    } else {
      membership = {
        ...membershipTypes[status].household,
        membershipTypeValue: 'household'
      }
    }

    setSelectedMembership(membership)
    setQuestionStep(4) // Show results
  }

  const handleProceedToForm = () => {
    setShowQuestionnaire(false)
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    // selectedMembership is already set from determineMembership()
  }

  const handleGoBack = () => {
    if (questionStep === 4) {
      setSelectedMembership(null)
      setAnswers(prev => ({ ...prev, isSingle: null }))
      setQuestionStep(3)
    } else if (questionStep === 3) {
      setAnswers(prev => ({ ...prev, isSenior: null }))
      setQuestionStep(2)
    } else if (questionStep === 2) {
      setAnswers(prev => ({ ...prev, isReturning: null }))
      setQuestionStep(1)
    }
  }

  const handleBackToMembership = () => {
    setShowQuestionnaire(false)
    setQuestionStep(1)
    setAnswers({
      isReturning: null,
      isSenior: null,
      isSingle: null
    })
    setSelectedMembership(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (showPayment && formData) {
    return (
      <SquarePayment
        formData={formData}
        membershipTypes={membershipTypes}
        paymentMethods={paymentMethods}
        onComplete={handlePaymentComplete}
        onBack={() => {
          setShowPayment(false)
          setShowForm(true)
        }}
      />
    )
  }

  if (showQuestionnaire) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-10">
        <div className="container-custom max-w-2xl mx-auto">
          <div className="card p-8">
            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">
                  Question {Math.min(questionStep, 3)} of 3
                </span>
                {questionStep > 1 ? (
                  <button
                    onClick={handleGoBack}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    &larr; Previous question
                  </button>
                ) : (
                  <button
                    onClick={handleBackToMembership}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Back to membership page
                  </button>
                )}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(Math.min(questionStep, 3) / 3) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Question 1: Returning or New */}
            {questionStep === 1 && (
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Are you a returning member?
                </h2>
                <p className="text-gray-600 mb-8">
                  Were you a member of Lockridge Forest Swim & Tennis Club in {new Date().getFullYear() - 1}?
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => handleAnswer('isReturning', true)}
                    className="btn btn-primary px-8 py-4 text-lg"
                  >
                    Yes, I'm Returning
                  </button>
                  <button
                    onClick={() => handleAnswer('isReturning', false)}
                    className="btn btn-secondary px-8 py-4 text-lg"
                  >
                    No, I'm New
                  </button>
                </div>
              </div>
            )}

            {/* Question 2: Senior (65+) */}
            {questionStep === 2 && (
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Are you 65 years or older?
                </h2>
                <p className="text-gray-600 mb-8">
                  Senior members (65+) qualify for our Single/Senior membership rate.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => handleAnswer('isSenior', true)}
                    className="btn btn-primary px-8 py-4 text-lg"
                  >
                    Yes, 65+
                  </button>
                  <button
                    onClick={() => handleAnswer('isSenior', false)}
                    className="btn btn-secondary px-8 py-4 text-lg"
                  >
                    No, Under 65
                  </button>
                </div>
              </div>
            )}

            {/* Question 3: Single or Household */}
            {questionStep === 3 && (
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Single or Household membership?
                </h2>
                <p className="text-gray-600 mb-8">
                  Household memberships cover all family members living at the same address.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => handleAnswer('isSingle', true)}
                    className="btn btn-primary px-8 py-4 text-lg"
                  >
                    Single Membership
                  </button>
                  <button
                    onClick={() => handleAnswer('isSingle', false)}
                    className="btn btn-secondary px-8 py-4 text-lg"
                  >
                    Household Membership
                  </button>
                </div>
              </div>
            )}

            {/* Question 4: Results */}
            {questionStep === 4 && selectedMembership && (
              <div>
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Perfect! We've found your membership
                  </h2>
                  <p className="text-gray-600">
                    Based on your answers, here's the membership that's right for you:
                  </p>
                </div>

                {/* Selected Membership Card */}
                <div className="card p-6 border-2 border-primary-500 bg-primary-50 mb-6">
                  <div className="text-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedMembership.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {selectedMembership.description}
                    </p>
                    <div className="flex items-baseline justify-center mb-2">
                      <span className="text-5xl font-bold text-primary-600">
                        ${selectedMembership.price}
                      </span>
                      <span className="text-gray-600 ml-2">/year</span>
                    </div>
                    {!isAfterMay1() && (
                      <div className="mb-3 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm font-semibold text-yellow-800">
                          Price increases by $100 on May 1st
                        </p>
                      </div>
                    )}
                    {(selectedMembership.initiationFee || isAfterMay1()) && (
                      <div className="text-sm space-y-1">
                        <p className="text-gray-600">
                          Annual Dues: ${selectedMembership.basePrice}
                        </p>
                        {selectedMembership.initiationFee && (
                          <p className="text-gray-600">
                            Initiation Fee: ${selectedMembership.initiationFee} (one-time)
                          </p>
                        )}
                        {isAfterMay1() && (
                          <p className="text-gray-600">
                            Late Joining Fee: $100 (after May 1st)
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="border-t border-primary-200 pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3 text-center">
                      What's Included:
                    </h4>
                    <ul className="space-y-2">
                      {selectedMembership.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleGoBack}
                    className="btn btn-secondary flex-1"
                  >
                    &larr; Go Back
                  </button>
                  <button
                    onClick={handleProceedToForm}
                    className="btn btn-primary flex-1"
                  >
                    Continue to Application
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (showForm) {
    return (
      <MembershipForm
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setShowForm(false)
          setSelectedMembership(null)
        }}
        membershipTypes={membershipTypes}
        preSelectedMembership={selectedMembership}
      />
    )
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-10">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {new Date().getFullYear()} Membership
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 mb-4">
              Join Lockridge Forest Swim Tennis Club
            </p>
            <p className="text-lg text-primary-50 max-w-2xl mx-auto mb-6">
              Enjoy year-round access to our Junior Olympic pool, four lighted tennis courts,
              and exclusive member events in a beautiful 9-acre park-like setting.
            </p>
            <button
              onClick={handleStartQuestionnaire}
              className="btn btn-secondary text-lg px-10 py-4 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Apply for Membership
            </button>
          </div>
        </div>
      </section>

      {/* Membership Benefits */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
              Member Benefits
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="flex justify-center mb-3">
                  <Waves className="w-12 h-12 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">Junior Olympic Pool</h3>
                <p className="text-gray-600 text-sm">
                  6-lane pool with diving board and separate wading pool
                </p>
              </div>
              <div className="text-center p-4">
                <div className="flex justify-center mb-3">
                  <Trophy className="w-12 h-12 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">Tennis Courts</h3>
                <p className="text-gray-600 text-sm">
                  Four lighted courts with ALTA, USTA, and T2 teams year-round
                </p>
              </div>
              <div className="text-center p-4">
                <div className="flex justify-center mb-3">
                  <Users2 className="w-12 h-12 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">Social Events</h3>
                <p className="text-gray-600 text-sm">
                  Exclusive member events, tournaments, and community gatherings
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Returning Members */}
      <section className="py-10 bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
            Returning Members
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
            {/* Household Membership */}
            <div className="card p-6 border-2 border-gray-200 hover:border-primary-500 transition-all">
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {membershipTypes.returning.household.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {membershipTypes.returning.household.description}
                </p>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-primary-600">
                    ${membershipTypes.returning.household.price}
                  </span>
                  <span className="text-gray-600 ml-2">/year</span>
                </div>
              </div>
              <ul className="space-y-2">
                {membershipTypes.returning.household.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Single/Senior Membership */}
            <div className="card p-6 border-2 border-gray-200 hover:border-primary-500 transition-all">
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {membershipTypes.returning.singleSenior.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {membershipTypes.returning.singleSenior.description}
                </p>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-primary-600">
                    ${membershipTypes.returning.singleSenior.price}
                  </span>
                  <span className="text-gray-600 ml-2">/year</span>
                </div>
              </div>
              <ul className="space-y-2">
                {membershipTypes.returning.singleSenior.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* New Members */}
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
            New Members
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* New Household Membership */}
            <div className="card p-6 border-2 border-gray-200 hover:border-primary-500 transition-all">
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {membershipTypes.new.household.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {membershipTypes.new.household.description}
                </p>
                <div className="flex items-baseline mb-1">
                  <span className="text-4xl font-bold text-primary-600">
                    ${membershipTypes.new.household.price}
                  </span>
                  <span className="text-gray-600 ml-2">/year</span>
                </div>
                <p className="text-xs text-gray-500">
                  Includes ${membershipTypes.new.household.initiationFee} initiation fee (one-time)
                </p>
              </div>
              <ul className="space-y-2">
                {membershipTypes.new.household.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* New Single/Senior Membership */}
            <div className="card p-6 border-2 border-gray-200 hover:border-primary-500 transition-all">
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {membershipTypes.new.singleSenior.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {membershipTypes.new.singleSenior.description}
                </p>
                <div className="flex items-baseline mb-1">
                  <span className="text-4xl font-bold text-primary-600">
                    ${membershipTypes.new.singleSenior.price}
                  </span>
                  <span className="text-gray-600 ml-2">/year</span>
                </div>
                <p className="text-xs text-gray-500">
                  Includes ${membershipTypes.new.singleSenior.initiationFee} initiation fee (one-time)
                </p>
              </div>
              <ul className="space-y-2">
                {membershipTypes.new.singleSenior.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Join Our Community?
          </h2>
          <p className="text-lg text-primary-50 mb-6 max-w-2xl mx-auto">
            Complete your membership registration online and enjoy all the benefits
            of being part of Lockridge Forest Swim Tennis Club.
          </p>
          <button
            onClick={handleStartQuestionnaire}
            className="btn btn-secondary text-lg px-10 py-4 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
          >
            Apply for Membership
          </button>
          <p className="mt-4 text-blue-100 text-sm">
            Questions? Email us at admin@lockridgeforestswimtennis.com or call (770) 609-7428
          </p>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-10 bg-gray-50">
        <div className="container-custom max-w-4xl mx-auto">
          <div className="card p-6">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">
              Important Information
            </h2>
            <div className="space-y-3 text-gray-700">
              <div className="flex items-start">
                <DollarSign className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm mb-1">Pool Season</h3>
                  <p className="text-gray-600 text-sm">Opens {poolSettings?.openingDate || 'TBD'}. Lifeguards on duty weekends {poolSettings?.weekendLifeguardHours || 'TBD'}, weekdays {poolSettings?.weekdayLifeguardHours || 'TBD'}.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Trophy className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm mb-1">Tennis Access</h3>
                  <p className="text-gray-600 text-sm">Four lighted courts available year-round. Court reservations required through the Tennis page.</p>
                </div>
              </div>
              <div className="flex items-start">
                <Users2 className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm mb-1">Facility Reservations</h3>
                  <p className="text-gray-600 text-sm">Tennis Pavilion and Taj Mahal available for member events. No reservation fee for members.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Membership
